<template>
  <div class="mt-2">
    <!-- Generate signature boxes for attendees -->
    <div
      v-if="attendees && attendees.length > 0"
      class="space-y-4 mt-4"
    >
      <div
        v-for="attendee in attendees"
        :key="attendee.id"
        class="w-full border rounded p-2 relative shadow-xs"
        style="height: auto"
      >
        <div class="flex items-center justify-between pb-2">
          <div class="flex items-center">
            <p class="pl-3">
              <span class="font-semibold text-gray-800">
                {{ attendee.attendeeName }}
                <span
                  v-if="attendee.attendeeType"
                  class="text-sm font-semibold text-gray-500 ml-2"
                >
                  {{ attendee.attendeeType }}
                </span>
              </span>
            </p>
          </div>
        </div>

        <!-- Signature area - always show both existing signature and add new option -->
        <div class="flex h-full gap-4">
          <!-- Original signature from attendee data (immutable) -->
          <div class="flex-1 flex flex-col">
            <div class="text-sm font-medium text-gray-600 mb-2">
              Original Signature:
            </div>
            <div
              v-if="attendee.signatureData"
              class="flex-1 border rounded bg-white flex items-center justify-center"
            >
              <img
                :src="attendee.signatureData"
                class="object-contain max-h-[80px] w-full"
              />
            </div>
            <div
              v-else
              class="flex-1 border rounded bg-gray-50 flex items-center justify-center text-gray-400 text-sm"
            >
              No original signature
            </div>
          </div>

          <!-- New signature input -->
          <div class="flex-1 flex flex-col">
            <div class="text-sm font-medium text-gray-600 mb-2">
              New Signature:
            </div>
            <div
              v-if="getSignatureForAttendee(attendee.id)"
              class="flex-1 border rounded bg-white flex items-center justify-center relative"
            >
              <img
                :src="getSignatureForAttendee(attendee.id).signatureData"
                class="object-contain max-h-[80px] w-full"
              />
              <!-- N/A indicator -->
              <div
                v-if="getSignatureForAttendee(attendee.id).isNotAvailable"
                class="absolute top-1 left-1 bg-red-100 text-red-800 text-xs px-2 py-1 rounded border border-red-200"
              >
                N/A
              </div>
              <!-- Remove button for new signature -->
              <Button
                icon="pi pi-times"
                class="absolute top-1 right-1 p-button-text p-button-sm text-red-500"
                :disabled="disabled"
                @click="
                  () => {
                    if (!disabled) {
                      removeSignatureForAttendee(attendee.id);
                    }
                  }
                "
                aria-label="Remove"
              />
            </div>
            <div
              v-else
              class="flex-1 border-2 border-dashed rounded flex flex-col justify-center items-center p-4"
              :style="{
                borderColor: invalid ? '#ef4444' : '#e5e7eb',
                backgroundColor: invalid ? '#fef2f2' : '#f9fafb'
              }"
            >
              <div class="text-sm font-medium text-gray-600 mb-4">
                Select Option:
              </div>
              <div class="flex gap-3 w-full">
                <Button
                  label="Sign"
                  icon="pi pi-pencil"
                  class="flex-1"
                  :disabled="disabled"
                  @click="
                    () => {
                      if (!disabled) {
                        openAttendeeSignatureDialog(attendee);
                      }
                    }
                  "
                />
                <Button
                  label="N/A"
                  icon="pi pi-times"
                  severity="secondary"
                  class="flex-1"
                  :disabled="disabled"
                  @click="
                    () => {
                      if (!disabled) {
                        setAttendeeNotAvailable(attendee);
                      }
                    }
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No attendees message -->
    <div
      v-if="!attendees || attendees.length === 0"
      class="text-center text-gray-500 py-4"
    >
      No attendees found. Please ensure the attendee data is available.
    </div>

    <!-- Attendee Signature Dialog -->
    <SignatureDialog
      v-if="attendeeSignatureObj.isVisible"
      v-model:visible="attendeeSignatureObj.isVisible"
      :skipAttendee="true"
      :attendeeName="currentAttendeeName"
      @onConfirm="attendeeSignatureObj.onConfirm"
    />
  </div>
</template>

<script>
  import { ref, defineComponent } from "vue";
  import SignatureDialog from "@/components/SignatureDialog.vue";
  import Helper from "@/utils/helper";

  export default defineComponent({
    name: "AttendeeSignatureField",
    components: {
      SignatureDialog
    },
    props: {
      attendees: {
        type: Array,
        default: () => []
      },
      disabled: {
        type: Boolean,
        default: false
      },
      invalid: {
        type: Boolean,
        default: false
      },
      // For FormInputComponent compatibility
      response: {
        type: Object,
        default: () => ({})
      },
      attendeeDataKey: {
        type: String,
        default: ""
      },
      updateStore: {
        type: Function,
        default: () => {}
      }
    },
    emits: ["update:signatures"],
    setup(props, { emit }) {
      const currentAttendeeName = ref("");

      // Create unified data structure for both dynamic and static forms
      const response = props.response || { attendees: props.attendees };
      const attendeeDataKey = props.attendeeDataKey || "attendees";
      const updateStore =
        props.updateStore || ((data) => emit("update:signatures", data));

      // Attendee signature dialog
      const attendeeSignatureObj = ref({
        isVisible: false,
        currentAttendee: null,
        onConfirm: (val) => onAttendeeSignatureConfirm(val)
      });

      const onAttendeeSignatureConfirm = (val) => {
        if (attendeeSignatureObj.value.currentAttendee) {
          // Create signOffData object with only changing fields
          const signOffData = {
            signatureData: val.signatureData,
            signatureDate: val.signatureDate,
            isNotAvailable: false
          };

          // Always use FormInputComponent logic
          // Get the attendee data array
          const attendeeData = Helper.findAndSetKeyInObject(
            response,
            attendeeDataKey
          );

          if (Array.isArray(attendeeData)) {
            // Find the attendee in the array
            const attendeeIndex = attendeeData.findIndex(
              (attendee) =>
                attendee.id === attendeeSignatureObj.value.currentAttendee.id
            );

            if (attendeeIndex >= 0) {
              // Create a new array to avoid circular references
              const updatedAttendeeData = [...attendeeData];

              // Update the attendee with signOffData
              updatedAttendeeData[attendeeIndex] = {
                ...updatedAttendeeData[attendeeIndex],
                signOffData: signOffData
              };

              // Update the response object
              Helper.findAndSetKeyInObject(
                response,
                attendeeDataKey,
                updatedAttendeeData
              );

              updateStore(
                Helper.findAndSetKeyInObject(response, attendeeDataKey)
              );
            }
          }
        }
        attendeeSignatureObj.value.currentAttendee = null;
      };

      const getSignatureForAttendee = (attendeeId) => {
        // Always use FormInputComponent logic
        // Get the attendee data array
        const attendeeData = Helper.findAndSetKeyInObject(
          response,
          attendeeDataKey
        );

        if (Array.isArray(attendeeData)) {
          // Find the attendee in the array
          const attendee = attendeeData.find(
            (attendeeItem) => attendeeItem.id === attendeeId
          );

          // Return the signOffData if it exists
          return attendee?.signOffData || null;
        }

        return null;
      };

      const removeSignatureForAttendee = (attendeeId) => {
        // Always use FormInputComponent logic
        // Get the attendee data array
        const attendeeData = Helper.findAndSetKeyInObject(
          response,
          attendeeDataKey
        );

        if (Array.isArray(attendeeData)) {
          // Find the attendee in the array
          const attendeeIndex = attendeeData.findIndex(
            (attendeeItem) => attendeeItem.id === attendeeId
          );

          if (attendeeIndex >= 0) {
            // Create a new array to avoid circular references
            const updatedAttendeeData = [...attendeeData];

            // Remove the signOffData from the attendee
            const updatedAttendee = { ...updatedAttendeeData[attendeeIndex] };
            delete updatedAttendee.signOffData;

            updatedAttendeeData[attendeeIndex] = updatedAttendee;

            // Update the response object
            Helper.findAndSetKeyInObject(
              response,
              attendeeDataKey,
              updatedAttendeeData
            );

            // Update the store with a deep copy to avoid circular references
            updateStore(
              Helper.findAndSetKeyInObject(response, attendeeDataKey)
            );
          }
        }
      };

      const openAttendeeSignatureDialog = (attendee) => {
        currentAttendeeName.value = attendee.attendeeName;
        attendeeSignatureObj.value.currentAttendee = attendee;
        attendeeSignatureObj.value.isVisible = true;
      };

      const setAttendeeNotAvailable = (attendee) => {
        // Create empty SVG data
        const emptySvgData =
          "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ3aGl0ZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9ImdyYXkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5OL0E8L3RleHQ+PC9zdmc+";

        // Create signOffData object with only changing fields
        const signOffData = {
          signatureData: emptySvgData,
          signatureDate: new Date().toISOString(),
          isNotAvailable: true
        };

        // Always use FormInputComponent logic
        // Get the attendee data array
        const attendeeData = Helper.findAndSetKeyInObject(
          response,
          attendeeDataKey
        );

        if (Array.isArray(attendeeData)) {
          // Find the attendee in the array
          const attendeeIndex = attendeeData.findIndex(
            (attendeeItem) => attendeeItem.id === attendee.id
          );

          if (attendeeIndex >= 0) {
            // Create a new array to avoid circular references
            const updatedAttendeeData = [...attendeeData];

            // Update the attendee with signOffData
            updatedAttendeeData[attendeeIndex] = {
              ...updatedAttendeeData[attendeeIndex],
              signOffData: signOffData
            };

            // Update the response object
            Helper.findAndSetKeyInObject(
              response,
              attendeeDataKey,
              updatedAttendeeData
            );

            // Update the store with a deep copy to avoid circular references
            updateStore(
              Helper.findAndSetKeyInObject(response, attendeeDataKey)
            );
          }
        }
      };

      return {
        attendeeSignatureObj,
        currentAttendeeName,
        getSignatureForAttendee,
        removeSignatureForAttendee,
        openAttendeeSignatureDialog,
        setAttendeeNotAvailable,
        Helper
      };
    }
  });
</script>
